export interface InsiderUser {
  uuid: string;
  email?: string;
  name?: string;
  surname?: string;
  username?: string;
  phone_number?: string;
  age?: number;
  gender?: string;
  birthday?: string;
  language?: string;
  country?: string;
  city?: string;
  region?: string;
  gsm?: string;
  has_transacted?: boolean;
  transaction_count?: number;
  gdpr_optin?: boolean;
  email_optin?: boolean;
  sms_optin?: boolean;
  whatsapp_optin?: boolean;
  returning?: boolean;
  list_id?: number[];
}

export interface InsiderPage {
  type: string;
}

export interface InsiderCategory {
  breadcrumb: string[];
}

export interface InsiderListing {
  items: InsiderListingItem[];
}

export interface InsiderListingItem {
  id: string;
  name: string;
  taxonomy: string[];
  currency: string;
  unit_price: number;
  unit_sale_price: number;
  url: string;
  stock?: number;
  color?: string;
  size?: string;
  product_image_url: string;
  custom?: Record<string, any>;
}

export interface InsiderProduct {
  id: string;
  name: string;
  locale?: string;
  taxonomy: string[];
  unit_price: number;
  unit_sale_price: number;
  url: string;
  in_stock?: number;
  product_image_url: string;
  group_code?: string;
  omnibus_price?: number;
  omnibus_discount?: number;
  discount?: Record<string, any>;
  sku?: string;
  stock?: number;
  color?: string;
  brand?: string;
  rating?: number;
  size?: string;
  gender?: string;
  product_type?: string;
  item_update_date?: string;
  item_start_date?: string;
  item_end_date?: string;
  description?: string;
  gtin?: string;
  multipack?: string;
  tags?: string[];
  category_path?: string;
  custom?: Record<string, any>;
}

export interface InsiderCart {
  total: number;
  items: InsiderCartItem[];
  shipping_cost?: number;
}

export interface InsiderCartItem {
  id: string;
  name: string;
  taxonomy: string[];
  unit_price: number;
  unit_sale_price: number;
  url: string;
  product_image_url: string;
  stock?: number;
  color?: string;
  size?: string;
  quantity: number;
}

export interface InsiderOrder {
  order_id: string;
  total: number;
  items: InsiderOrderProduct[];
  shipping_cost?: number;
}

export interface InsiderOrderProduct {
  id: string;
  name: string;
  taxonomy: string[];
  unit_price: number;
  unit_sale_price: number;
  url: string;
  product_image_url: string;
  stock?: number;
  color?: string;
  size?: string;
  quantity: number;
}

export interface InsiderDeliveryAddress {
  country?: string;
  city?: string;
  district?: string;
}

export interface InsiderEvent {
  event_name: string;
  event_data?: Record<string, any>;
}

interface InsiderQueueItem {
  type: string;
  value?: any;
}

export class InsiderService {
  static getInstance(): InsiderService {
    if (!InsiderService.instance) {
      InsiderService.instance = new InsiderService();
    }
    return InsiderService.instance;
  }
  private static instance: InsiderService;
  private isInitialized = false;

  init(): void {
    this.pushToQueue({ type: 'init' });
    this.isInitialized = true;
  }

  setCurrency(currency: string): void {
    this.pushToQueue({ type: 'currency', value: currency });
  }

  identify(user: InsiderUser): void {
    this.pushToQueue({ type: 'user', value: user });
  }

  trackPageView(page: InsiderPage): void {
    this.pushToQueue({ type: page.type }, true);
  }

  trackProductView(product: InsiderProduct): void {
    this.pushToQueue({ type: 'product', value: product }, true);
  }

  trackCategoryView(category: InsiderCategory): void {
    this.pushToQueue({ type: 'category', value: category }, true);
  }

  trackAddToCart(product: InsiderCartItem): void {
    this.pushToQueue({ type: 'add_to_cart', value: product }, true);
  }

  trackRemoveFromCart(product: InsiderCartItem): void {
    this.pushToQueue({ type: 'remove_from_cart', value: product }, true);
  }

  trackCartView(cart: InsiderCart): void {
    this.pushToQueue({ type: 'cart', value: cart }, true);
  }

  trackPurchase(order: InsiderOrder): void {
    this.pushToQueue({ type: 'purchase', value: order }, true);
  }

  trackEvent(event: InsiderEvent): void {
    this.pushToQueue({ type: 'event', value: event }, true);
  }

  trackSearch(query: string, results?: number): void {
    this.pushToQueue({ type: 'search', value: { query, results } }, true);
  }

  trackFormSubmission(formName: string, formData?: Record<string, any>): void {
    this.pushToQueue({ type: 'form_submission', value: { formName, formData } }, true);
  }

  trackButtonClick(buttonName: string, buttonData?: Record<string, any>): void {
    this.pushToQueue({ type: 'button_click', value: { buttonName, buttonData } }, true);
  }

  isLoaded(): boolean {
    return typeof window !== 'undefined' && typeof window.Insider !== 'undefined';
  }

  getInsider(): any {
    return typeof window !== 'undefined' ? window.Insider : null;
  }

  getInitialized(): boolean {
    return this.isInitialized;
  }

  private pushToQueue(item: InsiderQueueItem, shouldInit = false): void {
    if (typeof window !== 'undefined' && window.InsiderQueue) {
      window.InsiderQueue.push(item);
      if (shouldInit) {
        this.init();
      }
    }
  }
}

export const insiderService = InsiderService.getInstance();
